import React from 'react'
import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { accountSettingsPath } from '@/lib/account-settings'
import type { AccountAbilities, AccountSettingsSection } from '@/types/shared'

// Tab navigation for the Business settings panel. Follows the locked tab-nav convention: a <nav>
// of Inertia <Link>s styled as tabs — never Radix <Tabs> with onClick, never button + router.visit.
//
// WHICH TAB IS LIT IS A SERVER FACT, NOT A URL FACT — matching on the path lights the wrong tab
// with no error to notice. The server sends `activeSection` (AccountSettingsChrome::
// SETTINGS_SECTIONS) and this renders it; every href is the backend-served `settingsPath` plus a
// fixed suffix.
//
// Role-gated tabs (Groups, PMS connections, Billing & plan, Activity) are display hints only —
// the backend authorization seam remains the source of truth.

interface AccountSettingsNavProps {
  accountAbilities: AccountAbilities
  /** Server-decided selected tab. Never parsed from the path. */
  activeSection: AccountSettingsSection
  /** `/a/:account_slug/settings` — the root every tab hangs a fixed suffix off. */
  settingsPath: string
}

export default function AccountSettingsNav({ accountAbilities, activeSection, settingsPath }: AccountSettingsNavProps) {
  const tabs: { value: AccountSettingsSection; label: string; visible: boolean }[] = [
    { value: 'overview', label: 'Overview', visible: true },
    { value: 'team', label: 'Team', visible: true },
    // Combined inventory (the `groups` section) rides on the same tier as PMS connections and
    // Activity (owner/admin): it restructures which listing's configuration prices which rooms, so it
    // is not day-to-day pricing.
    { value: 'groups', label: 'Combined inventory', visible: accountAbilities.viewOperations },
    { value: 'pms-connections', label: 'PMS connections', visible: accountAbilities.viewOperations },
    { value: 'billing', label: 'Billing & plan', visible: accountAbilities.manageBilling },
    { value: 'activity', label: 'Activity', visible: accountAbilities.viewOperations },
  ]

  // Six tabs outgrow a phone, so the strip scrolls sideways instead of the page doing it (the
  // PricingRulesNav pattern).
  return (
    <div className="animate-slide-in max-w-full overflow-x-auto" style={{ animationDelay: '50ms' }}>
      <nav
        className="inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground"
        aria-label="Business settings"
      >
        {tabs
          .filter((tab) => tab.visible)
          .map((tab) => (
            <Link
              key={tab.value}
              href={accountSettingsPath(settingsPath, tab.value)}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
                activeSection === tab.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </Link>
          ))}
      </nav>
    </div>
  )
}
